'use client';

import { useEffect, useState } from 'react';
import { useUserContext } from '@instamenu/context-user';
import { useCheckoutContext } from '@instamenu/context-checkout';
import { Box, Label, Stack, Skeleton, SelectionModal, ModalNext } from '@instamenu/ui-system-next';
import { CardButton } from '../card-button/card-button';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { PaymentMethodCard } from '../payment-method-card/payment-method-card';
import { observer } from 'mobx-react-lite';
import { AddPaymentForm } from './add-payment-form';

export const PaymentMethod = observer(function PaymentMethod() {
  const { user, ready } = useUserContext();
  const order = useCheckoutContext()?.order;
  if (!order) throw new Error('Order is undefined');

  const [isOpen, setIsOpen] = useState(false);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(order.paymentMethod?.id);
  const [pendingPaymentMethodId, setPendingPaymentMethodId] = useState<string | undefined>();
  const paymentMethods = user?.paymentMethods ?? [];

  useEffect(() => {
    if (!ready || !user) return;
    user.refreshPaymentMethods();
  }, [ready, user]);

  useEffect(() => {
    if (
      pendingPaymentMethodId &&
      paymentMethods.some((method) => method.id === pendingPaymentMethodId)
    ) {
      setPendingPaymentMethodId(undefined);
    }

    if (
      order.paymentMethod?.id &&
      order.paymentMethod.id !== pendingPaymentMethodId &&
      !paymentMethods.some((method) => method.id === order.paymentMethod?.id)
    ) {
      order.clearPaymentMethod({ sync: false });
      setSelectedPaymentMethodId(undefined);
    }
  }, [order, order.paymentMethod?.id, paymentMethods, pendingPaymentMethodId]);

  const handleClose = () => {
    setIsOpen(false);
    setShowAddPaymentMethod(false);
  };

  if (!ready) {
    return (
      <Stack style={{ gap: 16 }}>
        <Skeleton variant="text" width={'50%'} height={22} />
        <Skeleton variant="rect" width={'100%'} height={themeVars.sizes.inputHeight.sm} />
      </Stack>
    );
  }

  return (
    <>
      {order.paymentMethod?.id ? (
        <>
          <Label>Payment Method</Label>
          <Box onClick={() => setIsOpen(true)} css={{ cursor: 'pointer' }}>
            <CardButton>
              <PaymentMethodCard paymentMethod={order.paymentMethod} />
            </CardButton>
          </Box>
        </>
      ) : (
        <Box css={{ paddingBottom: 20 }}>
          <CardButton
            label={paymentMethods.length === 0 ? 'Add payment method' : 'Select payment method'}
            isOpen={isOpen}
            onClick={() => {
              setIsOpen(true);
              if (paymentMethods.length === 0) setShowAddPaymentMethod(true);
            }}
          />
        </Box>
      )}

      {!showAddPaymentMethod && (
        <SelectionModal
          opened={isOpen}
          radioRight
          title="Select Payment Method"
          options={paymentMethods}
          value={selectedPaymentMethodId ?? null}
          onChange={(method) => setSelectedPaymentMethodId(method.id)}
          onClose={handleClose}
          onConfirm={() => {
            const selectedMethod = paymentMethods.find(
              (method) => method.id === selectedPaymentMethodId,
            );
            if (selectedMethod) {
              order.setPaymentMethod(selectedMethod);
              handleClose();
            }
          }}
          onAddNew={() => setShowAddPaymentMethod(true)}
          addNewButtonText="+ Add new payment method"
          confirmButtonText="Update"
          renderItem={(method) => <PaymentMethodCard paymentMethod={method} />}
        />
      )}

      <ModalNext
        opened={showAddPaymentMethod}
        containerStyle={{ padding: themeVars.pad[12] }}
        onRequestClose={handleClose}
      >
        <AddPaymentForm
          onBack={() => setShowAddPaymentMethod(false)}
          onSubmit={(paymentMethod) => {
            setIsOpen(false);
            order.setPaymentMethod(paymentMethod);
            setSelectedPaymentMethodId(paymentMethod.id);
            setPendingPaymentMethodId(paymentMethod.id);
            user?.refreshPaymentMethods();
            handleClose();
          }}
        />
      </ModalNext>
    </>
  );
});
